import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import api, { API_BASE_URL } from "../../config/api";

const BillingUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [dragActive, setDragActive] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [recipientUserId, setRecipientUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const user = useSelector((state) => state.auth.user);

  // Fetch users for billing recipient selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get(
          `${API_BASE_URL}/api/superadmin/users-for-billing`
        );
        if (response.data.success) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users", {
          position: "top-right"
        });
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [API_BASE_URL]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesChange(Array.from(e.dataTransfer.files));
    }
  };

  const validateFile = (file) => {
    // Validate file type (accept PDF, Excel, CSV)
    const validTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (!validTypes.includes(file.type)) {
      return { valid: false, error: `${file.name}: Invalid file type. Only PDF, Excel, or CSV allowed.` };
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      return { valid: false, error: `${file.name}: File size exceeds 10MB limit.` };
    }

    return { valid: true };
  };

  const handleFilesChange = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        // Check if file already exists in selected files
        const exists = selectedFiles.some(f => f.name === file.name && f.size === file.size);
        if (!exists) {
          validFiles.push(file);
        }
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      errors.forEach(err => toast.error(err, { position: "top-right" }));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesChange(Array.from(e.target.files));
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title for the billing document", {
        position: "top-right"
      });
      return;
    }

    if (!recipientUserId) {
      toast.error("Please select a recipient user", {
        position: "top-right"
      });
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file to upload", {
        position: "top-right"
      });
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    const parsedRecipientId = recipientUserId ? parseInt(recipientUserId, 10) : null;
    let successCount = 0;
    let failCount = 0;

    // Generate a single batch_id for all files being uploaded together
    const batchId = selectedFiles.length > 1 ? uuidv4() : null;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadProgress({ current: i + 1, total: selectedFiles.length });

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", title);
        formData.append("description", description);
        formData.append("uploaded_by", user.user_name);
        if (parsedRecipientId) {
          formData.append("recipient_user_id", parsedRecipientId);
        }
        if (batchId) {
          formData.append("batch_id", batchId);
        }

        const response = await api.post(
          `${API_BASE_URL}/api/billing/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to upload ${file.name}:`, response.data.error);
        }
      } catch (error) {
        failCount++;
        console.error(`Upload error for ${file.name}:`, error);
      }
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s)!`, {
        position: "top-right"
      });
      setSelectedFiles([]);
      setTitle("");
      setDescription("");
      setRecipientUserId("");
    }

    if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} file(s)`, {
        position: "top-right"
      });
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf text-red-500';
      case 'xlsx':
      case 'xls':
        return 'fa-file-excel text-green-600';
      case 'csv':
        return 'fa-file-csv text-blue-500';
      default:
        return 'fa-file text-gray-500';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8 font-poppins">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-blue mb-2">Billing Upload</h1>
          <p className="text-gray-600">
            Upload billing documents for processing (PDF, Excel, or CSV files)
          </p>
        </div>

        {/* Upload Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
          {/* Title and Description Form */}
          <div className="mb-8 space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title <span className="text-red">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter billing document title"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:outline-none transition-all duration-300 font-poppins"
                maxLength={255}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Send to User <span className="text-red">*</span>
              </label>
              {loadingUsers ? (
                <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500">
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Loading users...
                </div>
              ) : (
                <select
                  value={recipientUserId}
                  onChange={(e) => setRecipientUserId(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:outline-none transition-all duration-300 font-poppins bg-white cursor-pointer"
                >
                  <option value="">Select a user</option>
                  {users.map((userItem) => (
                    <option key={userItem.user_id} value={userItem.user_id}>
                      {userItem.user_name}
                      {userItem.first_name && userItem.last_name
                        ? ` (${userItem.first_name} ${userItem.last_name})`
                        : ""}
                      {" - "}
                      {userItem.role}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add any additional notes or description"
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:outline-none transition-all duration-300 font-poppins resize-none"
              />
            </div>
          </div>

          {/* Drag and Drop Zone */}
          <div
            className={`relative border-3 border-dashed rounded-2xl p-12 transition-all duration-300 ${
              dragActive
                ? "border-blue bg-blue/5"
                : "border-gray-300 hover:border-blue/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-blue/10 rounded-full flex items-center justify-center mb-6">
                <i className="fas fa-cloud-upload-alt text-4xl text-blue"></i>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Drop your files here
              </h3>
              <p className="text-gray-500 mb-6">
                or click to browse from your computer
              </p>

              <label
                htmlFor="file-upload"
                className="px-8 py-3 bg-blue text-white font-semibold rounded-xl hover:bg-blue/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
              >
                Choose Files
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".pdf,.xlsx,.xls,.csv"
                onChange={handleFileInputChange}
                className="hidden"
                multiple
              />

              <p className="text-sm text-gray-400 mt-4">
                Supported formats: PDF, Excel (.xlsx, .xls), CSV (Max 10MB each)
              </p>
            </div>
          </div>

          {/* Selected Files Display */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">
                  Selected Files ({selectedFiles.length})
                </h3>
                <button
                  onClick={handleClearAllFiles}
                  className="text-sm text-red hover:text-red/80 font-medium transition-colors"
                >
                  <i className="fas fa-trash-alt mr-1"></i>
                  Clear All
                </button>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="p-4 bg-gray-50 rounded-2xl border border-gray-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                          <i className={`fas ${getFileIcon(file.name)} text-xl`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 truncate text-sm">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="ml-4 w-8 h-8 bg-red/10 text-red rounded-lg hover:bg-red/20 transition-all duration-300 flex items-center justify-center"
                      >
                        <i className="fas fa-times text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-6 p-4 bg-blue/5 rounded-2xl">
              <div className="flex items-center gap-3 mb-2">
                <i className="fas fa-spinner fa-spin text-blue"></i>
                <span className="font-medium text-gray-700">
                  Uploading file {uploadProgress.current} of {uploadProgress.total}...
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {selectedFiles.length > 0 && !uploading && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className={`px-8 py-3 bg-yellow text-blue font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] ${
                  uploading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-yellow/90"
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="fas fa-upload"></i>
                  Upload {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Instructions Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-blue mb-4 flex items-center gap-3">
            <i className="fas fa-info-circle"></i>
            Upload Instructions
          </h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <i className="fas fa-check-circle text-green-500 mt-1"></i>
              <span>You can select and upload multiple files at once</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fas fa-check-circle text-green-500 mt-1"></i>
              <span>All files will be sent to the same recipient</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fas fa-check-circle text-green-500 mt-1"></i>
              <span>Supported file formats: PDF, Excel (.xlsx, .xls), and CSV</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fas fa-check-circle text-green-500 mt-1"></i>
              <span>Maximum file size is 10MB per file</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fas fa-check-circle text-green-500 mt-1"></i>
              <span>Drag and drop multiple files or click to browse</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BillingUpload;
